/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { NewCoreIconProps } from '@atlaskit/icon';
import BoardIcon from '@atlaskit/icon/core/board';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import TableIcon from '@atlaskit/icon/core/table';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

const dbIcons = [DatabaseIcon, TableIcon, GridIcon, BoardIcon];

const dbHandleChildCommands = (
	_experience: Experience,
	_command: NativeEmbedChildCommand,
): void => {};

const dbTransformUrl = (url: string, _isFullScreen?: boolean): string => {
	const urlObj = new URL(url);
	urlObj.searchParams.set('presentationMode', 'nativeEmbedEditorPlugin');
	return urlObj.toString();
};

const dbRenderLoadingComponent = (props: LoadingComponentProps): JSX.Element => (
	<LoadingOverlay
		animationDisabled={props.animationDisabled}
		icons={dbIcons}
		testId={props.testId}
	/>
);

export const dbToolbarItems: readonly ["askRovo", "summarise", "separator", "embed", "edit", "separator", "alignment", "separator", "openInNewWindow", "separator"] = [
	BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
	BUILTIN_TOOLBAR_KEYS.SUMMARISE,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	BUILTIN_TOOLBAR_KEYS.EMBED,
	BUILTIN_TOOLBAR_KEYS.EDIT,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
	BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
	BUILTIN_TOOLBAR_KEYS.SEPARATOR,
] as const;

export const dbMoreItems: ("alwaysShowTitle" | "copy" | "delete")[] = [
	BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
	BUILTIN_TOOLBAR_KEYS.COPY,
	BUILTIN_TOOLBAR_KEYS.DELETE,
];

export const dbEntryMoreItems: ("copy" | "delete")[] = [BUILTIN_TOOLBAR_KEYS.COPY, BUILTIN_TOOLBAR_KEYS.DELETE];

export const dbSharedUiConfig: {
    readonly appearance: "minimal"; readonly editorToolbarHandlers: {}; readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element; readonly showBorder: false; readonly showHeader: false; readonly titleIcon: {
        (props: NewCoreIconProps): JSX.Element;
        displayName: string;
    };
} = {
	editorToolbarHandlers: {},
	renderLoadingComponent: dbRenderLoadingComponent,
	titleIcon: DatabaseIcon,
	appearance: 'minimal',
	showHeader: false,
	showBorder: false,
} as const;

export const dbSharedManifestFields: { readonly handleChildCommands: (_experience: Experience, _command: NativeEmbedChildCommand) => void; readonly isActive: () => true; readonly lockResizeAspectRatio: false; readonly shouldAutoConvertToNativeEmbed: true; readonly supportedFeatures: Set<string>; readonly transformUrl: (url: string, _isFullScreen?: boolean) => string; } = {
	isActive: (): true => true,
	handleChildCommands: dbHandleChildCommands,
	shouldAutoConvertToNativeEmbed: true,
	lockResizeAspectRatio: false,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	transformUrl: dbTransformUrl,
} as const;
